import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../../components/Pagination";

const DEFAULT_ORDER_OPTIONS = [
  { id: "newest", name: "Newest" },
  { id: "oldest", name: "Oldest" },
];
const MINIMUM_LOADING_MS = 400;

function scrollToMain() {
  if (window.jQuery) {
    const main = window.jQuery(".main");

    if (main.length) {
      window.jQuery("html, body").animate({ scrollTop: main.offset().top }, 300);
      return;
    }
  }

  const main = document.querySelector(".main");

  if (main) {
    main.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function readQueryState(orderOptions, categories) {
  const searchParams = new URLSearchParams(window.location.search);
  const requestedCategory = searchParams.get("category");
  const requestedOrderBy = searchParams.get("orderBy");
  const requestedPage = Number.parseInt(searchParams.get("currentPage") || "", 10);

  const selectedCategory =
    categories.find((item) => String(item.Id) === String(requestedCategory)) || categories[0];

  const selectedOrder =
    orderOptions.find((item) => item.id === requestedOrderBy) || orderOptions[0];

  return {
    category: selectedCategory,
    orderBy: selectedOrder,
    currentPage: Number.isFinite(requestedPage) && requestedPage > 0 ? requestedPage : 1,
  };
}

function buildPageUrl(baseUrl, categoryId, orderById, currentPage) {
  const nextUrl = new URL(baseUrl, window.location.origin);

  nextUrl.searchParams.set("category", String(categoryId));
  nextUrl.searchParams.set("orderBy", orderById);
  nextUrl.searchParams.set("currentPage", String(currentPage));

  return `${nextUrl.pathname}${nextUrl.search}`;
}

function FilterDropdown({ id, label, value, items, activeKey, onSelect, renderKey, renderLabel }) {
  return (
    <div className="dropdown">
      <button
        className="btn-dropdown dropdown-toggle pr-0"
        type="button"
        id={id}
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {value}
      </button>
      <div className="dropdown-menu" aria-labelledby={id}>
        {items.map((item) => {
          const itemKey = renderKey(item);

          return (
            <button
              key={`${id}-${itemKey}`}
              className={`dropdown-item ${itemKey === activeKey ? "active" : ""}`}
              type="button"
              onClick={() => onSelect(item)}
            >
              {renderLabel(item)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NewsCard({ item }) {
  return (
    <div className="col-md-6 col-lg-4 mb-5 card-news blueBarBottom">
      <a href={item.url} className="link d-flex flex-column h-100">
        <div className="image-wrapper" style={{ backgroundImage: `url(${item.thumbnail})` }} />
        <div className="title h3 p-3">{item.title}</div>
        <div className="date mt-auto px-3 pb-3">{item.date}</div>
      </a>
    </div>
  );
}

export default function NewsIndexIsland({ config }) {
  const categories = useMemo(
    () => [{ Id: 0, Name: config.allCategoryLabel || "All" }, ...(Array.isArray(config.categories) ? config.categories : [])],
    [config.allCategoryLabel, config.categories],
  );
  const orderOptions = useMemo(
    () => (Array.isArray(config.orderOptions) && config.orderOptions.length > 0 ? config.orderOptions : DEFAULT_ORDER_OPTIONS),
    [config.orderOptions],
  );
  const initialState = useMemo(() => readQueryState(orderOptions, categories), [categories, orderOptions]);

  const [category, setCategory] = useState(initialState.category);
  const [orderBy, setOrderBy] = useState(initialState.orderBy);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [newsData, setNewsData] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(Number(config.pageSize) || 9);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    async function loadResults() {
      const loadingStartedAt = Date.now();
      document.documentElement.classList.add("loading");
      setLoading(true);

      try {
        const response = await fetch(config.newsApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          body: JSON.stringify({
            NodeId: config.nodeId,
            PageNumber: currentPage,
            OrderBy: orderBy.id,
            CategoryId: Number(category.Id) || 0,
          }),
        });

        if (!response.ok) {
          throw new Error(`News request failed with ${response.status}`);
        }

        const result = await response.json();

        if (!isActive) {
          return;
        }

        const nextPage = Number(result.pageNumber) || 1;

        setNewsData(Array.isArray(result.results) ? result.results : []);
        setTotal(Number(result.totalNumberOfRecords) || 0);
        setPageSize(Number(result.pageSize) || Number(config.pageSize) || 9);
        setCurrentPage(nextPage);

        const nextUrl = buildPageUrl(config.baseUrl, category.Id, orderBy.id, nextPage);
        window.history.pushState({ path: nextUrl }, document.title, nextUrl);
        scrollToMain();
      } catch (error) {
        if (!isActive) {
          return;
        }

        setNewsData([]);
        setTotal(0);
      } finally {
        if (!isActive) {
          return;
        }

        const remainingDelay = Math.max(0, MINIMUM_LOADING_MS - (Date.now() - loadingStartedAt));

        if (remainingDelay > 0) {
          await new Promise((resolve) => {
            window.setTimeout(resolve, remainingDelay);
          });
        }

        if (!isActive) {
          return;
        }

        setLoading(false);
        document.documentElement.classList.remove("loading");
      }
    }

    loadResults();

    return () => {
      isActive = false;
      document.documentElement.classList.remove("loading");
    };
  }, [category.Id, config.baseUrl, config.newsApiUrl, config.nodeId, config.pageSize, currentPage, orderBy.id]);

  return (
    <div className="container">
      <div className="row">
        <div className="col-md-6 col-xl-4 mb-3 d-flex flex-column flex-sm-row align-items-sm-center">
          <span className="my-0 mr-3 flex-shrink-0">{config.orderByLabel}</span>
          <FilterDropdown
            id="orderByDropdown"
            value={orderBy.name}
            items={orderOptions}
            activeKey={orderBy.id}
            onSelect={(item) => {
              setOrderBy(item);
              setCurrentPage(1);
            }}
            renderKey={(item) => item.id}
            renderLabel={(item) => item.name}
          />
        </div>
        {categories.length > 0 ? (
          <div className="col-md-6 col-xl-4 offset-xl-4 mb-3 d-flex flex-column flex-sm-row align-items-sm-center">
            <span className="my-0 mr-3 flex-shrink-0">{config.categoryLabel}</span>
            <FilterDropdown
              id="catDropdown"
              value={category.Name}
              items={categories}
              activeKey={String(category.Id)}
              onSelect={(item) => {
                setCategory(item);
                setCurrentPage(1);
              }}
              renderKey={(item) => String(item.Id)}
              renderLabel={(item) => item.Name}
            />
          </div>
        ) : null}
      </div>

      {!loading && newsData.length > 0 ? (
        <div id="newsCards" className="row mt-5">
          {newsData.map((item) => (
            <NewsCard key={item.nodeId} item={item} />
          ))}
        </div>
      ) : null}

      {!loading && newsData.length === 0 ? (
        <div className="row mt-5">
          <div className="col-md-6 col-xl-4 mb-3 d-flex flex-column flex-sm-row align-items-sm-center">
            <h2>{config.emptyStateText}</h2>
          </div>
        </div>
      ) : null}

      {total > (Number(config.paginationThreshold) || 3) ? (
        <div className="mt-4">
          <Pagination currentPage={currentPage} pageSize={pageSize} totalItems={total} onPageChange={setCurrentPage} />
        </div>
      ) : null}
    </div>
  );
}
